import React, { useMemo } from 'react';
import { apiClient } from 'src/api/client';
import { Helmet } from 'react-helmet';
import createStore from 'zustand';
import { CompanyProvider } from 'src/context/company-context';
import { Result } from 'src/antd';
import { TAllowedArcValue } from 'src/api/consts/CriptioArcValues';
import { T, useT } from '@transifex/react';
import { useQuery } from 'react-query';
import { isLocalhost, isVercel } from '../utils';
import { LoadingPage } from '../components/shared';
import { TBorderSize } from '../theme/borders';

type TValue = {
  id: string;
  name: string;
  phone_number: string;
  country: string;
  default_language: string;
  login_logo: string;
  login_method: 'none' | 'user' | 'bankid';
  login_method_user: 'pre_auth_search' | 'post_auth_search';
  logo: string;
  primary_color: string;
  secondary_color: string;
  waste_fractions: [
    {
      id: string;
      name: string;
      waste_category: string;
      waste_substance: string;
      waste_types: [
        {
          id: string;
          name: string;
        }
      ];
    }
  ];
  crm_custom_text_login_title: string;
  crm_custom_text_login: string;
  page_description_settings: {
    title?: string;
    page: 'login' | 'home';
    sections: {
      title?: string;
      description?: string;
      link_label?: string;
      link_url?: string;
      order?: number;
    }[];
  }[];
  crm_custom_text_home_title: string;
  crm_custom_text_home: string;
  crm_enable_container_notification_providers: string[];
  authentication_urls: {
    acr_values: TAllowedArcValue;
    url: string;
    business: boolean;
    provider: 'mitid' | 'nemid' | 'bankid';
  }[];
  crm_pdf_footer?: string;
  main_portal_font_family?: string;
  secondary_portal_font_family?: string;
  main_portal_font?: string;
  secondary_portal_font?: string;
  background_image_for_home_page?: string;
  background_image_for_login_page?: string;
  border_radius?: TBorderSize;
  supported_languages?: string[];
};

export type TCompanyStore = {
  data: TValue;
  hostname: string;
};

const useCompanyStore = createStore<TCompanyStore>(() => ({} as TCompanyStore));

export const useCompany = (): TCompanyStore => useCompanyStore();

export const CompanyStoreProvider = ({
  children,
}: {
  children: JSX.Element;
}): JSX.Element => {
  const t = useT();

  const searchParams = useMemo(
    () => new URLSearchParams(window.location.search),
    []
  );

  const hostname = useMemo(() => {
    if (window.WH_SS_PORTAL_DOMAIN) return window.WH_SS_PORTAL_DOMAIN;
    if (isVercel) {
      return (
        searchParams.get('crm_hostname') ||
        // eslint-disable-next-line no-alert
        window.prompt('Type a crmhostname', '') ||
        'portal-development.wastehero.io'
      );
    }

    if (isLocalhost) {
      return window.location.host;
    }
    return window.location.hostname.replace(/^www\./, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { isLoading, isError, error, data } = useQuery(
    ['CompanyStoreProvider', 'GET'],
    async () => {
      return apiClient.get(`company/${hostname}`).then((d) => {
        if (d.data.id) {
          useCompanyStore.setState({ data: d.data as TValue, hostname });
        }
        return d;
      });
    }
  );

  if ((error as $TSFixMe)?.response?.data?.detail === 'Not Found') {
    // the domain is not set to any company.
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100vw',
          height: '100%',
        }}
      >
        <Result
          status="404"
          title={
            <T
              _str="No DNS setup for hostname: {hostName}"
              hostName={hostname}
            />
          }
        />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100vw',
          height: '100%',
        }}
      >
        <Result
          status="500"
          title={<T _str="Error occured: Please try later." />}
        />
      </div>
    );
  }

  if (isLoading) {
    return <LoadingPage />;
  }

  const company = data?.data as TValue;

  return (
    <>
      <Helmet>
        <title>
          {t('Self-service - {companyName}', {
            companyName: company.name,
          }) || t('Citizen Portal')}
        </title>
        <link
          rel="icon"
          type="image/png"
          href={company.logo || 'https://not-found.png'} // to overwrite the previous cached logo of react-icon
          sizes="16x16"
        />
      </Helmet>
      <CompanyProvider>{children}</CompanyProvider>
    </>
  );
};
